<template>
  <GeojsonLayer
    v-if="
      sourcePoi &&
      (clickedItemType === 'realEstate' ||
        clickedItemType === 'poi' ||
        clickedItemType === 'line' ||
        clickedItemType === 'zone')
    "
    :before="before"
    :layer="layerStyle"
    :source="sourcePoi"
    :sourceId="sourceId"
    @layerClicked="onPoiClicked"
    @layerMouseEnter="handleMouseEnterPoi"
    @layerMouseMove="handleMouseMovePoi"
    @layerMouseLeave="handleMouseLeavePoi"
  />
  <GeojsonLayer
    v-if="sourcePoi && sourcePoiSelected"
    :before="layerId"
    :layer="layerStyleSelected"
    :layerId="layerIdSelected"
    :source="sourcePoiSelected"
    :sourceId="sourceIdSelected"
    @layerClicked="onPoiClicked"
    @layerMouseEnter="handleMouseEnterPoi"
    @layerMouseMove="handleMouseMovePoi"
    @layerMouseLeave="handleMouseLeavePoi"
  />
  <div v-show="false">
    <MapPopup ref="popupRef" :rows="[createSmallTitle(popupTitle)]" />
  </div>
</template>

<script lang="ts" setup>
// Imports
import { computed, ref, toRefs, watch } from 'vue'
import mapboxgl, {
  Popup,
  MapMouseEvent,
  GeoJSONFeature,
  CircleLayerSpecification,
  GeoJSONSourceSpecification
} from 'mapbox-gl'
import { useStore } from 'vuex'
import { PoiProperties } from '@/interfaces/poi.interface'
import GeojsonLayer from '@/components/map/mapbox-layers/GeojsonLayer.vue'
import MapPopup from '../../popups/MapPopup.vue'
import useMapbox from '@/composables/useMapbox'
import { createSmallTitle } from '@/components/Builder'
import { useGeneralStore } from '@/pinia'

// Props (defineProps)
interface Props {
  before: string
  layerId: string
  layerIdSelected: string
}
const props = defineProps<Props>()
const { before, layerId, layerIdSelected } = toRefs(props)
const store = useStore()
const { map } = useMapbox()
const piniaStore = useGeneralStore()

// Emits (defineEmits)
interface EMITS {
  (e: 'poiLayerCreated')
  (e: 'poiLayerRemoved')
}
const emits = defineEmits<EMITS>()

// Reactive variables (incl. composables)
const hoveredPoiId = ref('')
const popup = ref<Popup>(new mapboxgl.Popup({ closeButton: false, offset: 15 }))
const popupRef = ref<HTMLElement | null>(null)
const popupTitle = ref('')
const sourceId = ref('source-poi-outdoor-marker')
const sourceIdSelected = ref('source-poi-outdoor-marker-selected')
const colorSelectedItem = ref('rgba(250, 219, 20, 0.8)')

// Computed (store)
const clickedItemType = computed<string>(() => store.state.clickedItemType)
const isIndoorviewShowing = computed<boolean>(() => {
  return store.getters.isIndoorviewShowing
})
const isMeasurementActive = computed<boolean>(() => store.getters.isMeasurementActive)
const filteredMapPoiLayers = computed<[]>(() => store.state.mapview.filteredPoiLayers)
const outdoorPoisForSelectedRealEstate = computed<[]>(
  () => store.state.mapview.outdoorPoisForSelectedRealEstate
)
const selectedPoi = computed<PoiProperties>(() => store.state.selectedPoi)
const selectedRealEstateId = computed<string>(() => store.getters['mapview/selectedRealEstateId'])

const isBuildingSelected = computed(
  () => Object.keys(store.state.mapview.selectedBuilding).length > 0
)
const selectedAttachmentEntityId = computed<string>(() => {
  return piniaStore.selectedAttachment?.entityId ? piniaStore.selectedAttachment.entityId : ''
})
const selectedTicketEntityId = computed<string>(() => {
  return piniaStore.selectedTicket?.entityConnection?.length &&
    piniaStore.selectedTicket?.entityConnection[0].id
    ? piniaStore.selectedTicket.entityConnection[0].id
    : ''
})
// Computed (component specific)
const layerStyle = computed<CircleLayerSpecification>(() => {
  let guid = ''
  // Ivion poi:s don't contain guid
  if (selectedPoi.value) {
    guid = selectedPoi.value.guid
      ? selectedPoi.value.guid
      : selectedPoi.value.id
      ? selectedPoi.value.id
      : ''
  }
  return {
    id: layerId.value,
    source: sourceId.value,
    type: 'circle',
    maxzoom: 22.1,
    paint: {
      'circle-radius':
        clickedItemType.value === 'poi'
          ? [
              'interpolate',
              ['linear'],
              ['zoom'],
              16,
              ['case', ['==', ['get', 'guid'], guid], 4, 1.75],
              22,
              ['case', ['==', ['get', 'guid'], guid], 14, 11]
            ]
          : ['interpolate', ['linear', 1.75], ['zoom'], 16, 1.75, 22, 11],
      'circle-color': ['get', 'color'],
      'circle-stroke-color': [
        'case',
        ['==', ['get', 'guid'], selectedAttachmentEntityId.value],
        colorSelectedItem.value,
        ['==', ['get', 'guid'], selectedTicketEntityId.value],
        colorSelectedItem.value,
        '#FFF'
      ],
      'circle-stroke-width': [
        'interpolate',
        ['exponential', 1.75],
        ['zoom'],
        4,
        4,
        16,
        [
          'case',
          ['==', ['get', 'guid'], selectedAttachmentEntityId.value],
          8,
          ['==', ['get', 'guid'], selectedTicketEntityId.value],
          8,
          1
        ],
        20,
        [
          'case',
          ['==', ['get', 'guid'], selectedAttachmentEntityId.value],
          16,
          ['==', ['get', 'guid'], selectedTicketEntityId.value],
          16,
          2
        ],
        22,
        [
          'case',
          ['==', ['get', 'guid'], selectedAttachmentEntityId.value],
          22,
          ['==', ['get', 'guid'], selectedTicketEntityId.value],
          22,
          3
        ]
      ],
      'circle-stroke-opacity': ['case', ['boolean', ['==', ['get', 'guid'], guid], false], 1, 0.9],
      'circle-opacity': ['case', ['boolean', ['==', ['get', 'guid'], guid], false], 1, 0.9]
    }
  }
})
const layerStyleSelected = computed<CircleLayerSpecification>(() => {
  return {
    id: layerIdSelected.value,
    source: sourceIdSelected.value,
    type: 'circle',
    maxzoom: 22.1,
    paint: {
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 16, 4, 22, 11],
      'circle-color': ['get', 'color'],
      'circle-stroke-color': '#6A6A6A',
      'circle-stroke-width': ['interpolate', ['exponential', 1.75], ['zoom'], 4, 4, 20, 16, 22, 22],
      'circle-stroke-opacity': 0.9,
      'circle-opacity': 1
    }
  }
})
const sourcePoi = computed<GeoJSONSourceSpecification | null>(() => {
  if (!outdoorPoisForSelectedRealEstate.value?.length) return null
  let arrayOfVisiblePois = []
  for (const poiCategory of filteredMapPoiLayers.value) {
    for (const type of poiCategory.types) {
      if (type.visible === true) {
        arrayOfVisiblePois.push(type)
      }
    }
  }
  const visiblePois = outdoorPoisForSelectedRealEstate.value.filter((item) => {
    for (let type of arrayOfVisiblePois) {
      if (item.properties.typeId === type.id) return true
    }
    return false
  })
  return {
    type: 'geojson',
    data: {
      id: sourceId.value,
      type: 'FeatureCollection',
      features: visiblePois
    }
  }
})
const sourcePoiSelected = computed<GeoJSONSourceSpecification | null>(() => {
  if (!outdoorPoisForSelectedRealEstate.value?.length) return null
  const selectedOutdoorPoi = []
  if (sourcePoi.value && clickedItemType.value === 'poi') {
    const selectedPoiSource = sourcePoi.value.data.features.find(
      (poi) => poi.properties.guid === selectedPoi.value?.guid
    )
    if (selectedPoiSource) selectedOutdoorPoi.push(selectedPoiSource)
  }
  return {
    type: 'geojson',
    data: {
      id: sourceIdSelected.value,
      type: 'FeatureCollection',
      features: selectedOutdoorPoi
    }
  }
})

// Methods (component specific)
function handleMouseEnterPoi() {
  if (!isMeasurementActive.value) {
    setCursor('pointer')
  }
}
function handleMouseLeavePoi(event: MapMouseEvent) {
  if (!isMeasurementActive.value) {
    setCursor('')
    updateHoverPoi(event)
  }
}
function handleMouseMovePoi(event: MapMouseEvent) {
  if (!isMeasurementActive.value) {
    if (isFirstLayer(event)) {
      setCursor('pointer')
      updateHoverPoi(event)
    }
  }
}
function isFirstLayer(event: MapMouseEvent) {
  const renderedFeatures: GeoJSONFeature[] = map.queryRenderedFeatures(event.point)
  if (!renderedFeatures || !renderedFeatures.length) return false
  return (
    renderedFeatures[0].layer?.id === layerId.value ||
    renderedFeatures[0].layer?.id === layerIdSelected.value
  )
}
function onPoiClicked(event: MapMouseEvent) {
  if (isMeasurementActive.value || !isFirstLayer(event)) return
  const renderedFeatures = map?.queryRenderedFeatures(event.point)
  const poi = renderedFeatures?.find(
    (feature) =>
      feature.source === sourcePoi.value?.data?.id ||
      feature.source === sourcePoiSelected.value?.data?.id
  )
  if (!poi) return
  store.dispatch('CHANGE_POI', { poi: poi.properties })
  store.commit('SET_CLICKED_ITEM_TYPE', { type: 'poi' })
}
function setCursor(cursorType: string) {
  if (isMeasurementActive.value) return
  map.getCanvas().style.cursor = cursorType
}
function showPopup(event: MapMouseEvent) {
  if (!popupRef.value) return
  popup.value.setLngLat(event.lngLat).setDOMContent(popupRef.value.$el).addTo(map)
}
function updateHoverPoi(event: MapMouseEvent) {
  const features = event.features
  if (features && features.length > 0) {
    const foundPoi = features.find(
      (feature) =>
        feature.source === sourcePoi.value?.data?.id ||
        feature.source === sourcePoiSelected.value?.data?.id
    )
    if (!foundPoi) {
      popup.value.remove()
      return
    }
    if (hoveredPoiId.value !== foundPoi.id) {
      popupTitle.value = foundPoi.properties.name
      showPopup(event)
    }
    hoveredPoiId.value = foundPoi.id
  } else {
    hoveredPoiId.value = ''
    popup.value.remove()
  }
}

// Watchers
watch(
  isIndoorviewShowing,
  async () => {
    if (!isIndoorviewShowing.value && selectedPoi.value && !isBuildingSelected.value) {
      await store.dispatch('mapview/GET_OUTDOOR_POI_DATA_FOR_REAL_ESTATE', {
        categoryId: selectedPoi.value.categoryId,
        realEstateId: selectedRealEstateId.value,
        typeId: selectedPoi.value.typeId,
        checked: true
      })
    }
  },
  { immediate: true }
)
watch(sourcePoi, () => {
  if (!sourcePoi.value) emits('poiLayerRemoved')
  if (sourcePoi.value?.data?.features.length) emits('poiLayerCreated')
})

// Lifecycle Hooks
// Expose (defineExpose)
</script>
